import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, Search, ArrowUpRight, Lock } from "lucide-react";
import {
  REFERENCE_DATASET,
  REFERENCE_DATASET_VERSION,
  REFERENCE_LAST_UPDATED_ISO,
  getDrillPipe,
  getHWDP,
  getDrillCollars,
  getCasing,
  getTubing,
  getBits,
  getMudMotors,
  getRSS,
  getMWD,
  getLWD,
  getStabilizers,
  getCrossovers,
  getPumpsMerged,
  getBaseFluids,
  getMudAdditives,
  getMudTemplates,
  getCementSlurries,
  getBOPStacks,
  getFormationTemplates,
  getConstants,
} from "@/lib/reference";

// ============================================================================
// ReferenceLibraryBrowser — read-only browser surface for the merged library.
//
// One collapsible section per equipment kind + ancillary category. Each
// section renders a kind-specific column layout, a text filter, and a
// per-section provenance footer (source · version · count).
//
// Data is loaded via the existing selectors in @/lib/reference. The component
// does NOT mutate or persist anything. Used on /reference (Read More button).
// ============================================================================

const MIN_TOUCH = 44; // px

// ---------------------------------------------------------------------------
// Column specs — explicit per kind so the field-ordered request the user
// approved is enforced (especially DP/HWDP/Casing/Bit per user spec).
// Each col: { key, label, fmt? } — key can be a function for derived values.
// ---------------------------------------------------------------------------
const TUBULAR_DP_COLS = [
  { key: "nominal_size", label: "Nominal Size" },
  { key: "od_in", label: "OD (in)", num: 3 },
  { key: "id_in", label: "ID (in)", num: 3 },
  { key: "weight_lbft", label: "Weight (lb/ft)", num: 2 },
  { key: "grade", label: "Grade" },
  { key: "connection_type", label: "Connection" },
];

const TUBULAR_HWDP_COLS = TUBULAR_DP_COLS; // explicit alias per user spec
const TUBULAR_TUBING_COLS = TUBULAR_DP_COLS;

const TUBULAR_DC_COLS = [
  { key: "nominal_size", label: "Nominal Size" },
  { key: "od_in", label: "OD (in)", num: 3 },
  { key: "id_in", label: "ID (in)", num: 3 },
  { key: "weight_lbft", label: "Weight (lb/ft)", num: 2 },
  { key: "grade", label: "Grade" },
  { key: "connection_type", label: "Connection" },
];

const TUBULAR_CASING_COLS = [
  { key: "nominal_size", label: "Nominal Size" },
  { key: "od_in", label: "OD (in)", num: 3 },
  { key: "id_in", label: "ID (in)", num: 3 },
  { key: "drift_diameter_in", label: "Drift (in)", num: 3 },
  { key: "weight_lbft", label: "Weight (lb/ft)", num: 2 },
  { key: "grade", label: "Grade" },
  { key: "connection_type", label: "Connection" },
];

const BIT_COLS = [
  { key: "diameter_in", label: "Diameter (in)", num: 3 },
  { key: (it) => it.iadc_code || it.iadd_code || "—", label: "IADC Code" },
  { key: "tfa_sqin", label: "TFA (in²)", num: 3 },
  { key: "num_nozzles", label: "Nozzles" },
  { key: "notes", label: "Notes" },
];

const PUMP_COLS = [
  { key: "model", label: "Model" },
  { key: "type", label: "Type" },
  { key: "liner_size_in", label: "Liner (in)", num: 2 },
  { key: "pump_factor_bbl_stroke", label: "bbl/stk", num: 4 },
  { key: "max_pressure_psi", label: "Max P (psi)" },
  { key: "max_flow_gpm", label: "Max Q (gpm)" },
  { key: "volumetric_efficiency", label: "η" },
];

const MUD_MOTOR_COLS = [
  { key: "od_in", label: "OD (in)", num: 2 },
  { key: "length_ft", label: "Length (ft)" },
  { key: "bend_degrees", label: "Bend (°)", num: 2 },
  { key: "max_flow_gpm", label: "Max Q (gpm)" },
  { key: "max_torque_ftlb", label: "Max Torque (ft-lb)" },
  { key: "max_rpm", label: "Max RPM" },
  { key: "connection_type", label: "Connection" },
];

const RSS_COLS = [
  { key: "od_in", label: "OD (in)", num: 2 },
  { key: "length_ft", label: "Length (ft)" },
  { key: "max_dogleg_deg100ft", label: "Max DLS (°/100ft)", num: 2 },
  { key: "max_flow_gpm", label: "Max Q (gpm)" },
  { key: "max_rpm", label: "Max RPM" },
  { key: "connection_type", label: "Connection" },
];

const MWD_LWD_COLS = [
  { key: "od_in", label: "OD (in)", num: 2 },
  { key: "id_in", label: "ID (in)", num: 2 },
  { key: "length_ft", label: "Length (ft)" },
  { key: "max_flow_gpm", label: "Max Q (gpm)" },
  { key: "max_pressure_psi", label: "Max P (psi)" },
  { key: "connection_type", label: "Connection" },
  { key: "notes", label: "Notes" },
];

const STABILIZER_COLS = [
  { key: "od_in", label: "OD (in)", num: 2 },
  { key: "length_ft", label: "Length (ft)" },
  { key: "blade_type", label: "Blade Type" },
  { key: "blades", label: "Blades" },
  { key: "through_pipe_id_in", label: "Through ID (in)", num: 2 },
  { key: "connection_type", label: "Connection" },
];

const CROSSOVER_COLS = [
  { key: "top_connection", label: "Top" },
  { key: "bottom_connection", label: "Bottom" },
  { key: "od_in", label: "OD (in)", num: 2 },
  { key: "id_in", label: "ID (in)", num: 2 },
  { key: "length_in", label: "Length (in)" },
];

const BASE_FLUID_COLS = [
  { key: "name", label: "Name" },
  { key: "density_ppg", label: "Density (ppg)", num: 2 },
  { key: "viscosity_cp", label: "Viscosity (cP)", num: 2 },
];

const ADDITIVE_COLS = [
  { key: "name", label: "Name" },
  { key: "density_ppg", label: "Density (ppg)", num: 2 },
  { key: "units", label: "Units" },
  { key: "notes", label: "Notes" },
];

const MUD_TEMPLATE_COLS = [
  { key: "name", label: "Name" },
  { key: "pv_cp", label: "PV (cP)" },
  { key: "yp_lb100ft2", label: "YP (lb/100ft²)" },
  { key: "gel_10s", label: "Gel 10s" },
  { key: "gel_10min", label: "Gel 10min" },
  { key: "ph", label: "pH" },
  { key: "chlorides_ppm", label: "Cl⁻ (ppm)" },
  { key: "solids_pct", label: "Solids (%)" },
];

const CEMENT_COLS = [
  { key: "name", label: "Name" },
  { key: "density_ppg", label: "Density (ppg)", num: 2 },
  { key: "yield_cuft_sack", label: "Yield (ft³/sk)", num: 2 },
  { key: "thickening_time_hrs", label: "Thick. Time (hr)", num: 2 },
  { key: "temp_rating_f", label: "Temp (°F)" },
];

const BOP_COLS = [
  { key: "name", label: "Name" },
  { key: "type", label: "Type" },
  { key: "pressure_rating_psi", label: "WP (psi)" },
  { key: "bop_size_in", label: "Size (in)", num: 3 },
  { key: (it) => Array.isArray(it.rams) ? it.rams.length : 0, label: "Ram Slots" },
  { key: (it) => Array.isArray(it.annular) ? it.annular.length : 0, label: "Annulars" },
  { key: "choke_line_id_in", label: "Choke ID (in)", num: 3 },
  { key: "kill_line_id_in", label: "Kill ID (in)", num: 3 },
];

const FORMATION_COLS = [
  { key: "name", label: "Template Name" },
  { key: (it) => Array.isArray(it.sections) ? it.sections.length : 0, label: "Sections" },
];

const CONSTANT_COLS = [
  { key: "name", label: "Name" },
  { key: "value", label: "Value" },
  { key: "unit", label: "Unit" },
];

// ---------------------------------------------------------------------------
// Section registry — drives the render order and which selector to call.
// `manageCategory`: optional v2 Reference Libraries Hub key. When present,
// clicking the section header navigates to `/reference-libraries/{key}` where
// the user gets full CRUD (Add Record, edit, persist, USER vs SEED badge).
// Sections without a `manageCategory` remain read-only collapsible inline.
// ---------------------------------------------------------------------------
function _build(label, kind, selector, cols, opts = {}) {
  return { label, kind, selector, cols, ...opts };
}

const SECTIONS = [
  _build("Drill Pipe",        "drill_pipe",        getDrillPipe,        TUBULAR_DP_COLS,       { manageCategory: "drill_pipe" }),
  _build("HWDP",              "heavy_weight_drill_pipe", getHWDP,       TUBULAR_HWDP_COLS,     { manageCategory: "hwdp" }),
  _build("Drill Collars",     "drill_collar",      getDrillCollars,     TUBULAR_DC_COLS,       { manageCategory: "drill_collar" }),
  _build("Casing",            "casing",            getCasing,           TUBULAR_CASING_COLS,   { manageCategory: "casing" }),
  _build("Tubing",            "tubing",            getTubing,           TUBULAR_TUBING_COLS,   { manageCategory: "tubing" }),
  _build("Bits",              "bit",               getBits,             BIT_COLS,              { manageCategory: "bit" }),
  _build("Mud Motors",        "mud_motor",         getMudMotors,        MUD_MOTOR_COLS,        { manageCategory: "mud_motor" }),
  _build("Rotary Steerable",  "rotary_steerable",  getRSS,              RSS_COLS,              { manageCategory: "rotary_steerable" }),
  _build("MWD Tools",         "mwd_tool",          getMWD,              MWD_LWD_COLS,          { manageCategory: "mwd_tool" }),
  _build("LWD Tools",         "lwd_tool",          getLWD,              MWD_LWD_COLS,          { manageCategory: "lwd_tool" }),
  _build("Stabilizers",       "stabilizer",        getStabilizers,      STABILIZER_COLS,       { manageCategory: "stabilizer" }),
  _build("Crossovers",        "crossover",         getCrossovers,       CROSSOVER_COLS,        { manageCategory: "crossover" }),
  _build("Pumps",             "pump",              getPumpsMerged,      PUMP_COLS,             { manageCategory: "pump" }),
  _build("BOP Stacks",        "bop_stack",         getBOPStacks,        BOP_COLS,              { manageCategory: "bop" }),
  _build("Base Fluids",       "base_fluid",        getBaseFluids,       BASE_FLUID_COLS),
  _build("Mud Additives",     "mud_additive",      getMudAdditives,     ADDITIVE_COLS),
  _build("Mud Templates",     "mud_template",      getMudTemplates,     MUD_TEMPLATE_COLS),
  _build("Cement Slurries",   "cement_slurry",     getCementSlurries,   CEMENT_COLS),
  _build("Formation Templates","formation_template", getFormationTemplates, FORMATION_COLS),
  _build("Constants",         "constant",          getConstants,        CONSTANT_COLS),
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function _fmt(value, col) {
  if (value === undefined || value === null || value === "") return "—";
  if (col.num != null && typeof value === "number") return value.toFixed(col.num);
  if (typeof value === "number") return String(value);
  if (Array.isArray(value)) return value.join(", ");
  return String(value);
}

function _readCol(item, col) {
  if (typeof col.key === "function") return col.key(item);
  return item[col.key];
}

function _itemMatchesFilter(item, query) {
  if (!query) return true;
  const q = query.toLowerCase();
  for (const v of Object.values(item)) {
    if (v == null) continue;
    if (typeof v === "string" && v.toLowerCase().includes(q)) return true;
    if (typeof v === "number" && String(v).includes(q)) return true;
    if (Array.isArray(v) && v.some((s) => typeof s === "string" && s.toLowerCase().includes(q))) return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Section component
// ---------------------------------------------------------------------------
function Section({ label, kind, items, cols, manageCategory }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const filtered = useMemo(
    () => (query ? items.filter((it) => _itemMatchesFilter(it, query)) : items),
    [items, query]
  );

  // Managed categories surface a primary "Open library" navigation that
  // routes to the v2 Reference Libraries Hub (full CRUD: Add Record, edit,
  // persist, USER vs SEED badge). The inline expander remains as a quick peek.
  const managed = Boolean(manageCategory);

  return (
    <section
      data-testid={`ref-section-${kind}`}
      className="rounded-md border bg-[var(--rs-panel-2,#161b21)]"
      style={{ borderColor: "var(--rs-border,#1f2630)" }}
    >
      <div
        className="w-full flex items-center justify-between gap-2 px-3"
        style={{ minHeight: MIN_TOUCH }}
      >
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid={`ref-section-toggle-${kind}`}
          aria-expanded={open}
          className="flex items-center gap-2 min-w-0 flex-1 text-left"
          style={{ minHeight: MIN_TOUCH }}
        >
          {open ? (
            <ChevronDown className="w-4 h-4 shrink-0 text-[var(--rs-amber,#f5b400)]" />
          ) : (
            <ChevronRight className="w-4 h-4 shrink-0 text-[var(--rs-text-dim,#9aa6b2)]" />
          )}
          <span className="font-semibold text-sm text-[var(--rs-text,#e8edf2)] truncate">
            {label}
          </span>
          <span
            data-testid={`ref-section-count-${kind}`}
            className="font-mono-rs text-[11px] tracking-wider text-[var(--rs-amber,#f5b400)] shrink-0 ml-1"
          >
            {items.length} {items.length === 1 ? "item" : "items"}
          </span>
        </button>

        {managed ? (
          <Link
            to={`/reference-libraries/${manageCategory}`}
            data-testid={`ref-section-open-${kind}`}
            className="inline-flex items-center gap-1 shrink-0 px-2.5 py-1.5 rounded-md text-[11px] font-medium"
            style={{
              background: "var(--rs-amber, #f5b400)",
              color: "#0A0B0E",
              minHeight: 32,
            }}
            title={`Open ${label} library`}
          >
            Open <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        ) : (
          <span
            data-testid={`ref-section-readonly-${kind}`}
            className="inline-flex items-center gap-1 shrink-0 px-2 py-1 rounded-md text-[9.5px] uppercase tracking-[0.18em]"
            style={{
              background: "rgba(155,166,178,0.06)",
              color: "var(--rs-text-dim,#9aa6b2)",
              border: "1px solid var(--rs-border,#1f2630)",
              minHeight: 28,
            }}
            title="Browse-only · manual entry coming"
          >
            <Lock className="w-3 h-3" /> Browse
          </span>
        )}
      </div>

      {open && (
        <div className="px-3 pb-3 pt-1" data-testid={`ref-section-body-${kind}`}>
          {items.length > 4 && (
            <div className="flex items-center gap-2 mb-2.5">
              <Search className="w-3.5 h-3.5 text-[var(--rs-text-dim,#9aa6b2)] shrink-0" />
              <input
                type="text"
                data-testid={`ref-section-filter-${kind}`}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Filter ${items.length} items…`}
                className="input-rs flex-1 text-[12px]"
                style={{ height: MIN_TOUCH - 8 }}
              />
            </div>
          )}

          <div
            className="overflow-x-auto rounded-md"
            style={{ border: "1px solid var(--rs-border,#1f2630)" }}
          >
            <table className="min-w-full text-[11.5px]">
              <thead>
                <tr
                  className="text-[10px] uppercase tracking-[0.10em] text-[var(--rs-text-3,#6b7785)]"
                  style={{ background: "rgba(22,27,33,0.6)" }}
                >
                  <th className="text-left p-2 font-semibold whitespace-nowrap">ID</th>
                  {cols.map((c) => (
                    <th key={c.label} className="text-left p-2 font-semibold whitespace-nowrap">
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td
                      colSpan={cols.length + 1}
                      className="p-3 text-center text-[var(--rs-text-3,#6b7785)] text-[11px]"
                      data-testid={`ref-section-empty-${kind}`}
                    >
                      No items match the current filter.
                    </td>
                  </tr>
                ) : (
                  filtered.map((it) => (
                    <tr
                      key={it.id}
                      data-testid={`ref-row-${kind}-${it.id}`}
                      className="border-t"
                      style={{ borderColor: "rgba(148,163,184,0.14)" }}
                    >
                      <td className="p-2 font-mono-rs text-[10.5px] text-[var(--rs-text-dim,#9aa6b2)] whitespace-nowrap">
                        {it.id}
                      </td>
                      {cols.map((c) => (
                        <td
                          key={c.label}
                          className="p-2 font-mono-rs text-[var(--rs-text,#e8edf2)] whitespace-nowrap"
                        >
                          {_fmt(_readCol(it, c), c)}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div
            data-testid={`ref-section-footer-${kind}`}
            className="mt-2 text-[10px] text-[var(--rs-text-3,#6b7785)] font-mono-rs tracking-wide flex items-center gap-1.5 flex-wrap"
          >
            <span>Source: {REFERENCE_DATASET}</span>
            <span>·</span>
            <span>Version {REFERENCE_DATASET_VERSION}</span>
            <span>·</span>
            <span>Updated {REFERENCE_LAST_UPDATED_ISO}</span>
            <span>·</span>
            <span>
              Showing {filtered.length} of {items.length}
            </span>
          </div>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Top-level browser
// ---------------------------------------------------------------------------
export default function ReferenceLibraryBrowser() {
  // Snapshot all selectors once at mount. The merged library loader is
  // memoized so this is effectively a no-op after first call.
  const data = useMemo(() => SECTIONS.map((s) => ({ ...s, items: s.selector() })), []);

  return (
    <div className="mt-6" data-testid="ref-library-browser">
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <h2 className="text-lg font-bold text-[var(--rs-text,#e8edf2)]">
          Browse Library Contents
        </h2>
        <span className="text-[10px] uppercase tracking-[0.08em] text-[var(--rs-text-3,#6b7785)]">
          Read-only · Verify against your tally
        </span>
      </div>
      <p
        className="text-[11px] text-[var(--rs-text-dim,#9aa6b2)] leading-relaxed mb-3"
        data-testid="ref-library-browser-intro"
      >
        Expand any section to inspect the equipment records, fluids, cement
        slurries, BOP stacks, formation templates, and constants currently
        wired into the calculator pickers. Reference values must be verified
        against rig-specific tally books and manufacturer specifications
        before operational use.
      </p>

      <div className="flex flex-col gap-2">
        {data.map((s) => (
          <Section
            key={s.kind}
            label={s.label}
            kind={s.kind}
            items={s.items}
            cols={s.cols}
            manageCategory={s.manageCategory}
          />
        ))}
      </div>
    </div>
  );
}
